import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { DEFAULT_LEGAL_DOC_TYPES } from "./LegalDocumentFormModal";
import ROUTE_PATH from "../../constants/routes";

export default function ManageableDocTypeSelect({
  value,
  onChange,
  onBlur,
  name = "idDanhMuc",
  initialDocTypes = [],
  defaultList = DEFAULT_LEGAL_DOC_TYPES,
  placeholder = "-- Chọn --",
  emptyText = "Không tìm thấy dữ liệu phù hợp",
  error,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [docTypes, setDocTypes] = useState(() => {
    return initialDocTypes && initialDocTypes.length > 0
      ? initialDocTypes
      : defaultList;
  });

  const containerRef = useRef(null);

  // Sync when the parent finishes loading categories from the API.
  useEffect(() => {
    const syncCategories = () => {
      if (initialDocTypes && initialDocTypes.length > 0) {
        setDocTypes(initialDocTypes);
      } else {
        setDocTypes(defaultList);
      }
    };

    syncCategories();
  }, [initialDocTypes, defaultList]);

  // Find selected item object
  const selectedItem =
    docTypes.find((t) => t.id === value) ||
    (defaultList && defaultList.find((t) => t.id === value));

  const [inputValue, setInputValue] = useState(selectedItem ? selectedItem.name : "");

  // Sync display text when value or selectedItem changes when dropdown is NOT active
  useEffect(() => {
    if (!isOpen) {
      setInputValue(selectedItem ? selectedItem.name : "");
    }
  }, [value, selectedItem, isOpen]);

  // Click outside to close dropdown and revert input text if no new selection was made
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setInputValue(selectedItem ? selectedItem.name : "");
        if (onBlur) onBlur();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, selectedItem, onBlur]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputValue(text);
    if (!isOpen) setIsOpen(true);

    // If input is completely cleared by user, clear the selection value
    if (text.trim() === "" && value) {
      if (onChange) {
        onChange({ target: { name, value: "" } });
      }
    }
  };

  const handleSelect = (item) => {
    if (onChange) {
      onChange({ target: { name, value: item.id } });
    }
    setInputValue(item.name);
    setIsOpen(false);
  };

  const filteredList = docTypes.filter((t) =>
    (t.name || "").toLowerCase().includes(inputValue.trim().toLowerCase())
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Combobox Main Input */}
      <div
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm bg-white transition-all ${
          disabled ? "bg-gray-100 cursor-not-allowed opacity-75" : "hover:border-blue-400"
        } ${
          isOpen
            ? "ring-2 ring-blue-500 border-blue-500"
            : error
            ? "border-red-500"
            : "border-gray-300"
        }`}
      >
        <input
          type="text"
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 pr-2 cursor-text"
        />
        <ChevronDown
          onClick={() => {
            if (!disabled) setIsOpen((prev) => !prev);
          }}
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 cursor-pointer ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </div>

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {/* Dropdown Menu (No inner search box) */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 py-1.5 z-[99999] animate-in fade-in zoom-in-95 duration-100">
          {/* List of Items */}
          <div className="max-h-[220px] overflow-y-auto py-1 space-y-0.5">
            {filteredList.length === 0 ? (
              <div className="px-4 py-3 text-center text-xs text-gray-500">
                {emptyText}
              </div>
            ) : (
              filteredList.map((item) => {
                const isSelected = value === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={`group flex items-center justify-between px-3 py-2 text-xs cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
                      {isSelected && <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                      <span className="truncate">{item.name}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Navigation Link to Management Page */}
          <div className="px-3 pt-2 mt-1 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
            <span>Tổng: {filteredList.length} mục</span>
            <Link
              to={ROUTE_PATH.DOCUMENTS_CATEGORIES}
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium hover:underline cursor-pointer"
            >
              <Layers className="w-3 h-3" />
              <span>Quản lý danh mục</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
